import MediaCarousel from './mediaCarousel';
import useMediaQuery from "@material-ui/core/useMediaQuery";
import theme from "../../../theme";
import parsePrices from "../../../../functions/parseSWPrices";
import { useEffect, useState } from "react";

function getSeason() {
	const month = new Date().getMonth() + 1; // 0-based in JS
	if ([1, 2, 3, 9, 10, 11, 12].includes(month)) return "carousel-fall-winter";
	if ([4, 5, 6, 7, 8].includes(month)) return "carousel-spring-summer";
	return "carousel";
}

function isChristmas() {
	const month = new Date().getMonth() + 1; // 0-based in JS
	return [11, 12, 1].includes(month);
}

function isValidImage(image, coverId) {
	if (!image) return false;

	const media = image.media;
	if (!media) return false;
	if (media?.metaData?.height < 1000 || media?.metaData?.width < 1000) return false;

	const displayTypes = media?.translated?.customFields?.display_types;
	if (!displayTypes) return false;
	if (displayTypes.length === 0) return false;
	if (displayTypes.length === 1 && displayTypes[0].includes('list')) return false;
	if (displayTypes.length === 1 && displayTypes[0].includes('christmas') && !isChristmas()) return false;
	if (image.id === coverId && displayTypes.length === 1) return false; // removes dedicated list images

	// skip if not mp4 but also missing thumbnails or metadata
	if (
		media.url.indexOf(".mp4") < 0 &&
		(media.thumbnails.length === 0 || media.metadata === null)
	) {
		return false;
	}

	return true;
}

export default function ProductGallery({coverId, media, product}) {
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
	const prices = parsePrices(product.calculatedPrices && product.calculatedPrices.length > 0 ? product.calculatedPrices[0] : product.calculatedPrice);
	const [parsedMedia, setParsedMedia] = useState(parseMedia());

	useEffect(() => {
		setParsedMedia(parseMedia());
	}, [media])

	function parseMedia() {
		let fullImages = [];
		let thumbImages = [];
		const christmas = isChristmas();
		const season = getSeason();

		const finalGallery = media.reduce((acc, img) => {
			const types = img?.media?.translated?.customFields?.display_types || [];

			// Skip invalid images immediately
			if (!isValidImage(img, coverId)) return acc;

			if (img.media.url.includes('.mp4') && types.length > 0) {
				acc.video.push(img);
				return acc;
			}

			if (types.includes('carousel-christmas') && !types.includes('list-source') && christmas) {
				acc.christmas.push(img);
			} else if (types.includes(season)) {
				acc.season.push(img);
			} else if (types.includes('carousel')) {
				acc.standard.push(img);
			}

			return acc;
		}, { media: [], standard: [], season: [], christmas: [], video: [], list: [] });

		// Merge according to your logic
		const mergedStandard =
			finalGallery.season.length >= 2
				? [...finalGallery.christmas, ...finalGallery.season]
				: [ ...finalGallery.christmas, ...finalGallery.season, ...finalGallery.standard];

		// Combine media + merged standard
		const result = [...mergedStandard, ...finalGallery.media];

		if (finalGallery.video.length > 0) {
			const video = finalGallery.video.filter(img => img?.media?.customFields?.display_types?.includes(season))?.[0];
			result.unshift(video)
		}

		result.forEach(image => {
			if (!image) return;

			let fullImage = { alt: product.translated.name, imageType: image.media?.translated?.customFields?.display_types?.indexOf('original') >= 0 ? 'canvas' : 'carousel' };
			let thumbImage = { alt: product.translated.name };

			if (image.media.url.indexOf('.mp4') > 0) {
				fullImage.src = image.media.url;
				fullImage.type = 'video';
				thumbImage.type = 'video';
			}

			let largestYet = 0;
			image.media.thumbnails.forEach((thumb, index) => {
				if ((thumb.width >= 840 && thumb.width > largestYet) || index === 0) {
					largestYet = thumb.width;
					fullImage.magnify = {
						src: image.media.url,
						width: image.media.metaData.width,
						height: image.media.metaData.height
					};
					fullImage.width = thumb.width;
					fullImage.height = (image.media.metaData.height / image.media.metaData.width) * thumb.width;
					fullImages.aspectRatio = image.media.metaData.width / image.media.metaData.height;
					fullImage.src = thumb.url;
				}

				if (thumb.width === 120 || index === 0) {
					thumbImage.src = thumb.url;

					if (image.media.metaData.width < image.media.metaData.height) {
						thumbImage.height = thumb.height;
						thumbImage.width = (image.media.metaData.width / image.media.metaData.height) * thumb.width;
					} else if (image.media.metaData.width > image.media.metaData.height) {
						thumbImage.width = thumb.width;
						thumbImage.height = (image.media.metaData.height / image.media.metaData.width) * thumb.width;
					} else {
						thumbImage.width = thumb.width;
						thumbImage.height = thumb.height;
					}
				}
			});

			fullImages.push(fullImage);
			thumbImages.push(thumbImage);
		});

		if(fullImages.length === 0) {
			const cover = product?.cover?.media;
			if (!cover)	return;

			fullImages.push({
				alt: product.translated.name,
				src: cover.url,
				width: cover?.metaData?.width,
				height: cover?.metaData?.height
			});

			thumbImages.push({
				alt: product.translated.name,
				src: cover.url,
				width: cover?.metaData?.width,
				height: cover?.metaData?.height
			});
		}

		return {full: fullImages, thumbnails: thumbImages};
	}

	function getFirstImageAspectRatio() {
		let aspectRatio = 1;
		let index = 0;

		if (media[index].media.metaData === null) {
			do {
				index = index + 1;
			} while(media[index].media.metaData === null || index >= media.length);
		}

		if(media.length > 0) {
			aspectRatio = media[index].media.metaData.height / media[index].media.metaData.width;
		}

		return aspectRatio;
	}

	function isOnSale() {
		return prices.isOnSale;
	}

	if(!media || media.length === 0) {
		return null;
	}

	return (
		<MediaCarousel
			product={product}
			media={parsedMedia}
			productNumber={product.productNumber}
			isOnSale={isOnSale()}
			thumbnails={true}
			arrows={!isMobile}
			thumbnailPosition={isDesktop ? 'left' : 'bottom'}
			aspectRatio={getFirstImageAspectRatio()}
			videoProps={{
				controls: false,
				autoPlay: true,
				muted: true,
				loop: true,
				style: {
					display: 'block',
					width: '100%',
				}
			}}/>
	);
}
